import { isSafeReturnTo, submitOauthSessionBridge } from "PFApp/helpers/oauth_session_bridge";
import { useIsSignedIn } from "PFApp/hooks";
import { useAppLayoutStoreActions } from "PFApp/stores/app_layout_store";
import { useAuthStoreActions } from "PFApp/stores/auth_store";
import useBookingModulePermissions from "PFApp/use_booking_module_permissions";
import useIsAuditPlannerPermitted from "PFApp/use_is_audit_planner_permitted";
import { useIsMarketplacePermitted } from "PFApp/use_is_marketplace_permitted";
import useIsWorkflowPermitted from "PFApp/use_is_workflow_permitted";
import { LoadingDots } from "PFComponents/loading_dots";
import { useStoreMsTeamsLastVisitedPath } from "PFCore/helpers/ms_teams";
import { isSaasAdmin } from "PFCore/helpers/profile";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import useIsPermittedTo, { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useStringParamValue } from "PFCore/hooks";
import { useCurrentAccount } from "PFCore/hooks/queries/account/use_current_account";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { PermissionRule } from "PFTypes";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { startsWith } from "underscore.string";

import { useDeeplink } from "../use_deeplink";
import { isAccountSwitched, isRegistrationComplete, shouldShowTerms } from "./utils";

export const WithLayout = ({ children, layout = { sidebar: "sidebar" } }) => {
  const { setLayout } = useAppLayoutStoreActions();

  useEffect(() => {
    setLayout(layout.sidebar);
  }, []);

  return children;
};

export const EnsureLoggedIn = ({ children }) => {
  const location = useLocation();
  const { isSignedIn } = useIsSignedIn();

  const { clearTokens } = useAuthStoreActions();
  useStoreMsTeamsLastVisitedPath();
  const { saveDeeplink } = useDeeplink();

  useEffect(() => {
    if (!isSignedIn) {
      if (location.pathname !== "/sign_out") {
        saveDeeplink();
      }
      clearTokens();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  if (isSignedIn) {
    return children;
  } else {
    return <Navigate to="/users/signin" replace />;
  }
};

EnsureLoggedIn.propTypes = {
  children: PropTypes.node.isRequired
};

const OAUTH_BRIDGE_FALLBACK_MS = 10000;

export const EnsureLoggedOut = ({ children }) => {
  const { isSignedIn } = useIsSignedIn();
  const { getAccessToken } = useAuthStoreActions();
  const returnTo = useStringParamValue("return_to");
  const safeReturnTo = returnTo && isSafeReturnTo(returnTo) ? returnTo : null;
  const [bridgeFailed, setBridgeFailed] = useState(false);

  useEffect(() => {
    if (!isSignedIn || !safeReturnTo) {
      return undefined;
    }

    const fallback = window.setTimeout(() => setBridgeFailed(true), OAUTH_BRIDGE_FALLBACK_MS);
    submitOauthSessionBridge(getAccessToken(), safeReturnTo);
    return () => window.clearTimeout(fallback);
  }, [isSignedIn, safeReturnTo, getAccessToken]);

  if (!isSignedIn) {
    return children;
  }

  if (safeReturnTo && !bridgeFailed) {
    return <LoadingDots pageViewEnabled centered circlesEnabled qaId="EnsureLoggedOut.BridgeLoading" />;
  }

  return <Navigate to="/" replace />;
};

EnsureLoggedOut.propTypes = {
  children: PropTypes.node.isRequired
};

export const EnsureRegistrationComplete = ({ children }) => {
  const location = useLocation();
  const { data: currentAccount } = useCurrentAccount();

  const { data: currentProfile } = useCurrentProfile();

  if (currentProfile && shouldShowTerms(currentAccount, currentProfile)) {
    return <Navigate to="/terms" replace />;
  } else if (currentProfile && isRegistrationComplete(currentProfile)) {
    const path = location.pathname;
    const isAdminPath = startsWith(path, "/admin") || startsWith(path, "/saas_admin");

    if (!isAdminPath && isAccountSwitched(currentProfile, currentAccount)) {
      return <Navigate to="/admin" replace />;
    }

    return children;
  } else {
    return <Navigate to="/onboarding" replace />;
  }
};

EnsureRegistrationComplete.propTypes = {
  children: PropTypes.node.isRequired
};

export const EnsureRegistrationNotComplete = ({ children }) => {
  const { data: currentProfile } = useCurrentProfile();

  if (!isRegistrationComplete(currentProfile)) {
    return children;
  } else {
    return <Navigate to="/" replace />;
  }
};

EnsureRegistrationNotComplete.propTypes = {
  children: PropTypes.node.isRequired
};

export const EnsureFeatureEnabled = ({ feature, redirectTo = "/", children }) => {
  const isEnabled = useIsFeatureEnabled();

  if (isEnabled(feature)) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

EnsureFeatureEnabled.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string,
  feature: PropTypes.string
};

export const EnsureProfilePermission = ({ permittedTo, redirectTo = "/", enabled = true, children }) => {
  const { data: currentProfile } = useCurrentProfile();

  const isPermittedTo = useIsPermittedTo(currentProfile);

  if (!enabled || isPermittedTo(permittedTo)) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

EnsureProfilePermission.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string,
  permittedTo: PropTypes.string,
  enabled: PropTypes.bool
};

export const EnsureBookingModulePermission = ({ redirectTo = "/", children }) => {
  const { isEnabled, isPermittedToAccess } = useBookingModulePermissions();

  if (isEnabled && isPermittedToAccess) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

EnsureBookingModulePermission.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string
};

export const EnsureWorkflowPermission = ({ redirectTo = "/", children }) => {
  const isWorkflowPermitted = useIsWorkflowPermitted();

  if (isWorkflowPermitted) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

EnsureWorkflowPermission.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string
};

export const EnsureTransferPermission = ({ redirectTo = "/", children }) => {
  const isAllowedToTransferEngagements = useIsCurrentUserPermittedTo(PermissionRule.WorkflowTransfer);

  if (isAllowedToTransferEngagements) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

export const EnsureAuditPlannerPermission = ({ redirectTo = "/", children }) => {
  const auditPlannerPermitted = useIsAuditPlannerPermitted();

  if (auditPlannerPermitted) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

EnsureAuditPlannerPermission.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string
};

export const EnsureIsAdmin = ({ children, limitUserManager }) => {
  const { data: currentProfile } = useCurrentProfile();

  const isPermittedTo = useIsPermittedTo(currentProfile);

  if (limitUserManager && isPermittedTo(PermissionRule.UserManager)) {
    return <Navigate to="/" replace />;
  } else if (currentProfile.roles && currentProfile.roles.indexOf("admin") >= 0) {
    return children;
  } else {
    return <Navigate to="/" replace />;
  }
};

EnsureIsAdmin.propTypes = {
  children: PropTypes.node.isRequired,
  limitUserManager: PropTypes.bool
};

export const EnsureIsSaasAdmin = ({ children }) => {
  const { data: currentProfile } = useCurrentProfile();

  if (isSaasAdmin(currentProfile)) {
    return children;
  } else {
    return <Navigate to="/" replace />;
  }
};

EnsureIsSaasAdmin.propTypes = {
  children: PropTypes.node.isRequired
};

export const EnsureLoggedInAndRegistered = ({ children }) => (
  <EnsureLoggedIn>
    <EnsureRegistrationComplete>{children}</EnsureRegistrationComplete>
  </EnsureLoggedIn>
);

EnsureLoggedInAndRegistered.propTypes = {
  children: PropTypes.node.isRequired
};

export const EnsureMarketplacePermission = ({ redirectTo = "/", children }) => {
  const isMarketplacePermitted = useIsMarketplacePermitted();

  if (isMarketplacePermitted) {
    return children;
  } else {
    return <Navigate to={redirectTo} replace />;
  }
};

EnsureMarketplacePermission.propTypes = {
  children: PropTypes.node.isRequired,
  redirectTo: PropTypes.string
};
